import { Group } from '@components/layout';
import { Tag } from '@components/tag';
import { Stack, StackProps } from '@mui/material';
import { useField } from 'formik';
import { useState } from 'react';
import { TextInput } from '../TextInput';
import { FormFieldError } from './FormFieldError';

type FormTagsInputProps = {
  containerProps?: StackProps;
  fullwidth?: boolean;
  maintainGutters?: boolean;
  name: string;
  label?: string;
  labelInfo?: string;
};

export const FormTagsInput = ({
  containerProps = {},
  fullwidth = true,
  maintainGutters = true,
  name,
  label,
  labelInfo,
}: FormTagsInputProps) => {
  const [tagInput, setTagInput] = useState<string>('');
  const [field, meta, helpers] = useField<string[]>(name);
  const error = meta?.touched && meta?.error;

  const checkTagInput = (value: string, autoSubmit: boolean) => {
    if (autoSubmit || (value[value.length - 1] === ' ' && value.length > 1)) {
      if (!field.value.includes(value.trim())) {
        void helpers.setValue([...field.value, value.trim()]);
      }
      setTagInput('');
    } else {
      setTagInput(value);
    }
  };

  const handleTagDelete = (tag: string) => {
    void helpers.setValue(field.value.filter((t) => t !== tag));
  };

  return (
    <Stack sx={{ width: fullwidth ? '100%' : 'fit-content', ...containerProps.sx }}>
      <TextInput
        label={label || 'Tags'}
        labelInfo={labelInfo}
        fullWidth={fullwidth}
        sx={{
          flexWrap: 'wrap',
          maxWidth: fullwidth ? null : '400px',
          pt: '3px',
          '& .MuiInputBase-input': { flex: 1, minWidth: '50px' },
        }}
        startAdornment={
          <Group flexWrap="wrap" rowGap={1}>
            {field.value.map((tag) => (
              <Tag
                key={tag}
                label={tag}
                onDelete={() => {
                  handleTagDelete(tag);
                }}
                sx={{
                  height: '21px',
                  '& .MuiChip-label': { fontSize: '12px', lineHeight: '21px' },
                }}
              />
            ))}
          </Group>
        }
        value={tagInput}
        onChange={(e) => {
          checkTagInput(e.target.value, false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Backspace' && (e.target as HTMLInputElement).value === '') {
            handleTagDelete(field.value[field.value.length - 1]);
          }
          if (e.key === 'Tab' && (e.target as HTMLInputElement).value !== '') {
            checkTagInput((e.target as HTMLInputElement).value, true);
          }
        }}
        onBlur={(e) => {
          if (e.target.value !== '') {
            checkTagInput(e.target.value, true);
          }
        }}
      />
      <FormFieldError error={error} maintainGutters={maintainGutters} />
    </Stack>
  );
};
